import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useMerchant } from "@auth";
import { getPaymentProfiles } from "@api";

const PaymentProfilesContext = createContext();

export const usePaymentProfilesContext = () => {
  const context = useContext(PaymentProfilesContext);

  if (context === undefined) {
    throw new Error(
      "usePaymentProfilesContext must be used within a PaymentProfilesProvider",
    );
  }

  return context;
};

export const PaymentProfilesProvider = ({ children }) => {
  const { merchant } = useMerchant();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!merchant?.id) {
      setProfiles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getPaymentProfiles();
      setProfiles(data ?? []);
    } catch (error) {
      console.error("Failed to fetch payment profiles:", error);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [merchant?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getProfileById = useCallback(
    (id) => profiles.find((p) => p.id === id),
    [profiles],
  );

  const refresh = useCallback(() => fetchData(), [fetchData]);

  const value = {
    profiles,
    loading,
    getProfileById,
    refresh,
  };

  return (
    <PaymentProfilesContext.Provider value={value}>
      {children}
    </PaymentProfilesContext.Provider>
  );
};
